import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Form, Formik, FormikProps } from "formik";
import moment from "moment";
import { createColumnHelper } from "@tanstack/react-table";
import { LabelModes } from "../../../../ui-components/molecules/labeled-inputs/labeledInput";
import LabeledDateRangeInput from "../../../../ui-components/molecules/labeled-inputs/labeled-date-range-input/labeledDateRangeInput";
import XGSFormSelect from "../../../../ui-components/form/select/xgsFormSelect";
import Table from "../../../../ui-components/table/table";
import Button, { ButtonThemes } from "../../../../ui-components/button/button";
import XGSErrorMessage from "../../../../ui-components/error-message/errorMessage";
import { userSelector } from "../../../../slices/user/userSlice";
import {
  pickupAssignmentSelector
} from "../../../../slices/pickup/pickupAssignmentSlice";
import ServiceCentersState from "../../../../slices/service-centers/ServiceCentersState";
import {
  serviceCentersSelector,
  getRegionsServiceCenters
} from "../../../../slices/service-centers/serviceCentersSlice";
import { XGSSelectOption } from "ui-components/xgs-select/xgsSelect";
import {
  PickupsLogFilterSchema,
  TerminalViewPickupSummaryFilterListModel,
  TIME_PERIOD_LIST
} from "../../../../app/data/pickup/models";
import { PicklistsSummary } from "./pickup-summary/pickupSummary";
import "./terminalViewPickup.scss";
import { usePickupSummary, usePickupSummaryTiles, useOriginTerminals, useDestinationTerminals } from "../../../../hooks/usePickupSummary";
import LabeledSelectInput from "ui-components/molecules/labeled-inputs/labeled-select-input/labeledSelectInput";
import TerminalViewPickupDetails from "./TerminalViewDetails/terminalViewPickupDetails";

interface TerminalViewPickupSummaryProps {
  filterRefTerminal?: React.MutableRefObject<any>;
}

const columnHelper = createColumnHelper<any>();

const TerminalViewPickupSummary: React.FC<TerminalViewPickupSummaryProps> = ({ filterRefTerminal }) => {


  const userState = useSelector(userSelector);
  const currentTerminalCodeFetch = userState.activeTerminal?.id;

  const initialValues: TerminalViewPickupSummaryFilterListModel = {
    currentTerminalCode: currentTerminalCodeFetch,
    timePeriod: "THIS_WEEK",
    from: "",
    to: "",
    region: "",
    originTerminalNumbers: [],
    destinationTerminalNumbers: [],
  };

  const dispatch = useDispatch();
  const serviceCentersState: ServiceCentersState = useSelector(serviceCentersSelector);

  const getDateRangeForTimePeriod = (timePeriod: string) => {
    const today = moment();

    switch (timePeriod) {
      case "THIS_WEEK":
        return {
          from: today.clone().startOf("week").toDate(), // Sunday
          to: today.clone().endOf("week").toDate(),     // Saturday
        };
      case "LAST_WEEK":
        return {
          from: today.clone().subtract(1, "week").startOf("week").toDate(),
          to: today.clone().subtract(1, "week").endOf("week").toDate(),
        };
      case "THIS_MONTH":
        return {
          from: today.clone().startOf("month").toDate(),
          to: today.clone().endOf("month").toDate(),
        };
      case "LAST_MONTH":
        return {
          from: today.clone().subtract(1, "month").startOf("month").toDate(),
          to: today.clone().subtract(1, "month").endOf("month").toDate(),
        };
      case "THIS_QUARTER":
        return {
          from: today.clone().startOf("quarter").toDate(),
          to: today.clone().endOf("quarter").toDate(),
        };
      case "LAST_QUARTER":
        return {
          from: today.clone().subtract(1, "quarter").startOf("quarter").toDate(),
          to: today.clone().subtract(1, "quarter").endOf("quarter").toDate(),
        };
      case "CUSTOM":
      default:
        return { from: undefined, to: undefined };
    }
  };

  const initializeFilter = (): TerminalViewPickupSummaryFilterListModel => {
    const defaultFilter = { ...initialValues };

    if (defaultFilter.timePeriod === "THIS_WEEK") {
      const { from, to } = getDateRangeForTimePeriod("THIS_WEEK");
      defaultFilter.from = from?.toISOString() ?? undefined;
      defaultFilter.to = to?.toISOString() ?? undefined;
    }

    return defaultFilter;
  };


  const [filter, setFilter] = useState<TerminalViewPickupSummaryFilterListModel>(initializeFilter());
  const [terminalSearch, setTerminalSearch] = useState("");
  const [destTerminalSearch, setDestTerminalSearch] = useState("");
  const { data: originterminalOptions = [], isLoading: originterminalsLoading } = useOriginTerminals(terminalSearch);
  const { data: destinationterminalOptions = [], isLoading: destinationterminalsLoading } = useDestinationTerminals(destTerminalSearch);
  const [terminalViewNumber, setTerminalViewNumber] = useState<number | null>(null);
  const [popupFilterData, setPopupFilterData] = useState<TerminalViewPickupSummaryFilterListModel>({});

  const currentTerminalCode = useMemo(() => {
    if (!userState.activeTerminal?.id) return 0;
    return userState.activeTerminal.id;
  }, [userState.activeTerminal?.id]);


  const pickupAssignmentState = useSelector(pickupAssignmentSelector);
  const filterFormRef = useRef<FormikProps<TerminalViewPickupSummaryFilterListModel>>(null);

  const getRegionsOptions = useCallback(() => {
    if (!serviceCentersState.regions) return [];
    return serviceCentersState.regions.map((region) => ({
      label: region.regionName,
      value: region.id.toString(),
    }));
  }, [serviceCentersState.regions]);

  const onFilterSubmit = (data: TerminalViewPickupSummaryFilterListModel) => {
    const terminals: XGSSelectOption[] = Array.isArray(data.originTerminalNumbers) ? data.originTerminalNumbers : [];
    const destterminals: XGSSelectOption[] = Array.isArray(data.destinationTerminalNumbers) ? data.destinationTerminalNumbers : [];

    const regionOption = data.region;

    const regionLabel = typeof regionOption === "object" && regionOption !== null
      ? regionOption.label?.toString() || ""
      : regionOption?.toString() || "";

    const formattedFilterData = {
      ...data,
      from: data.from?.toApiDateFormat(),
      to: data.to?.toApiDateFormat(),
      region: regionLabel || "",
      originTerminalNumbers: terminals,
      destinationTerminalNumbers: destterminals
    };
    setPopupFilterData(formattedFilterData);

    setFilter({
      ...data,
      originTerminalNumbers: terminals,
      destinationTerminalNumbers: destterminals
    });

    refetchList(formattedFilterData);
    refetchTiles(formattedFilterData);
  };

  useEffect(() => {
    dispatch(getRegionsServiceCenters());
    if (filter.timePeriod === "THIS_WEEK") {
      onFilterSubmit(filter);
    }
    // eslint-disable-next-line
  }, []);

  const pickupSummaryFilter = useMemo(() => {
    if (!popupFilterData || Object.keys(popupFilterData).length === 0) {
      const regionOption = getRegionsOptions().find(opt => opt.value === filter.region);
      const regionLabel = regionOption?.label || "";

      return {
        currentTerminalCode,
        timePeriod: filter.timePeriod || "CUSTOM",
        from: filter.from ? filter.from.toApiDateFormat() : "",
        to: filter.to ? filter.to.toApiDateFormat() : "",
        originTerminalNumbers: Array.isArray(filter.originTerminalNumbers) && filter.originTerminalNumbers.length > 0
          ? filter.originTerminalNumbers.map(t => t.value).join(",")
          : "",
        destinationTerminalNumbers: Array.isArray(filter.destinationTerminalNumbers) && filter.destinationTerminalNumbers.length > 0
          ? filter.destinationTerminalNumbers.map(t => t.value).join(",")
          : "",
        region: regionLabel,
      };
    }

    const isCustom = (popupFilterData?.timePeriod || filter.timePeriod) === "CUSTOM";

    return {
      ...popupFilterData,
      originTerminalNumbers: Array.isArray(filter.originTerminalNumbers) && filter.originTerminalNumbers.length > 0
        ? filter.originTerminalNumbers.map(t => t.value).join(",")
        : "",
      destinationTerminalNumbers: Array.isArray(filter.destinationTerminalNumbers) && filter.destinationTerminalNumbers.length > 0
        ? filter.destinationTerminalNumbers.map(t => t.value).join(",")
        : "",
      from: isCustom ? popupFilterData.from?.toApiDateFormat() : undefined,
      to: isCustom ? popupFilterData.to?.toApiDateFormat() : undefined,
    };
  }, [popupFilterData, currentTerminalCode, filter, getRegionsOptions]);

  const {
    data: pickupSummaryList,
    isLoading: listLoading,
    refetch: refetchList
  } = usePickupSummary(pickupSummaryFilter, { enabled: false });


  const {
    data: pickupSummaryTiles,
    isLoading: tilesLoading,
    refetch: refetchTiles
  } = usePickupSummaryTiles(pickupSummaryFilter, { enabled: false });


  useEffect(() => {
    if (filterRefTerminal) {
      filterRefTerminal.current = pickupSummaryFilter;
    }
  }, [pickupSummaryFilter, filterRefTerminal]);

  const onShowViewDetails = (terminalNum: number, status?: string) => {
    setTerminalViewNumber(terminalNum);
    setPopupFilterData((prev) => ({ ...prev, status }));
  };

  const columns = [
    columnHelper.accessor("terminalName", {
      id: "terminalName",
      header: "Terminal Name",
      size: 100,
    }),
    columnHelper.accessor("created", {
      id: "created",
      header: "Created",
      cell: (cellProps) => (
        <span
          className="xgs-table-link-cell"
          onClick={(e) => {
            e.stopPropagation();
            onShowViewDetails(cellProps?.row?.original?.terminalNumber, "Created");
          }}
        >
          {cellProps.getValue()}
        </span>
      ),
      size: 30,
    }),
    columnHelper.accessor("completed", {
      id: "completed",
      header: "Completed",
      cell: (cellProps) => (
        <span
          className="xgs-table-link-cell"
          onClick={(e) => {
            e.stopPropagation();
            onShowViewDetails(cellProps?.row?.original?.terminalNumber, "Completed");
          }}
        >
          {cellProps.getValue()}
        </span>
      ),
      size: 30,
    }),
    columnHelper.accessor("missed", {
      id: "missed",
      header: "Missed",
      cell: (cellProps) => (
        <span
          className="xgs-table-link-cell"
          onClick={(e) => {
            e.stopPropagation();
            onShowViewDetails(cellProps?.row?.original?.terminalNumber, "Missed");
          }}
        >
          {cellProps.getValue()}
        </span>
      ),
      size: 30,
    }),
    columnHelper.accessor("rescheduled", {
      id: "rescheduled",
      header: "Rescheduled",
      cell: (cellProps) => (
        <span
          className="xgs-table-link-cell"
          onClick={(e) => {
            e.stopPropagation();
            onShowViewDetails(cellProps?.row?.original?.terminalNumber, "Rescheduled");
          }}
        >
          {cellProps.getValue()}
        </span>
      ),
      size: 30,
    }),
    columnHelper.accessor("cancelled", {
      id: "cancelled",
      header: "Canceled",
      cell: (cellProps) => (
        <span
          className="xgs-table-link-cell"
          onClick={(e) => {
            e.stopPropagation();
            onShowViewDetails(cellProps?.row?.original?.terminalNumber, "Canceled");
          }}
        >
          {cellProps.getValue()}
        </span>
      ),
      size: 30,
    }),

  ];

  const getTimePeriodFromDates = (from?: string, to?: string): string => {
    if (!from || !to) return "CUSTOM";

    const fromDate = moment(from);
    const toDate = moment(to);
    const today = moment();

    // if (fromDate.isSame(today, "day") && toDate.isSame(today, "day")) {
    //   return "TODAY";
    // }

    // if (
    //   fromDate.isSame(today.clone().subtract(1, "day"), "day") &&
    //   toDate.isSame(today.clone().subtract(1, "day"), "day")
    // ) {
    //   return "YESTERDAY";
    // }

    // 🔹 Sunday–Saturday logic
    if (
      fromDate.isSame(today.clone().startOf("week"), "day") &&
      toDate.isSame(today.clone().endOf("week"), "day")
    ) {
      return "THIS_WEEK";
    }

    if (
      fromDate.isSame(today.clone().subtract(1, "week").startOf("week"), "day") &&
      toDate.isSame(today.clone().subtract(1, "week").endOf("week"), "day")
    ) {
      return "LAST_WEEK";
    }

    if (
      fromDate.isSame(today.clone().startOf("month"), "day") &&
      toDate.isSame(today.clone().endOf("month"), "day")
    ) {
      return "THIS_MONTH";
    }

    if (
      fromDate.isSame(today.clone().subtract(1, "month").startOf("month"), "day") &&
      toDate.isSame(today.clone().subtract(1, "month").endOf("month"), "day")
    ) {
      return "LAST_MONTH";
    }

    if (
      fromDate.isSame(today.clone().startOf("quarter"), "day") &&
      toDate.isSame(today.clone().endOf("quarter"), "day")
    ) {
      return "THIS_QUARTER";
    }

    if (
      fromDate.isSame(today.clone().subtract(1, "quarter").startOf("quarter"), "day") &&
      toDate.isSame(today.clone().subtract(1, "quarter").endOf("quarter"), "day")
    ) {
      return "LAST_QUARTER";
    }

    return "CUSTOM";
  };

  const getTimePeriodListValues = () => {
    return Object.keys(TIME_PERIOD_LIST).map((key: string) => {
      return {
        value: key,
        label: TIME_PERIOD_LIST[key as keyof typeof TIME_PERIOD_LIST]
      };
    });
  };

  const getOriginTerminalOptions = (selectedOptions: XGSSelectOption[] = []) => {
    const selectedValues = selectedOptions.map((opt) => opt.value);

    const options: XGSSelectOption[] = [];

    originterminalOptions
      .filter((item) => !selectedValues.includes(item.value))
      .forEach((item) => {
        options.push({
          label:
            item.label.charAt(0) +
            item.label.slice(1),
          value: item.value,
        });
      });

    return options;
  };

  const getDestinationTerminalOptions = (selectedOptions: XGSSelectOption[] = []) => {
    const selectedValues = selectedOptions.map((opt) => opt.value);

    const options: XGSSelectOption[] = [];

    destinationterminalOptions
      .filter((item) => !selectedValues.includes(item.value))
      .forEach((item) => {
        options.push({
          label:
            item.label.charAt(0) +
            item.label.slice(1),
          value: item.value,
        });
      });

    return options;
  };

  const handleTimePeriodChange = (
    timePeriodOption: XGSSelectOption | null,
    props: FormikProps<TerminalViewPickupSummaryFilterListModel>
  ) => {
    const timePeriod = timePeriodOption?.value?.toString() ?? undefined;

    props.setFieldValue("timePeriod", timePeriod);

    const { from, to } = getDateRangeForTimePeriod(timePeriod ?? "CUSTOM");

    const fromDateStr = from?.toISOString();
    const toDateStr = to?.toISOString();

    props.setFieldValue("from", fromDateStr);
    props.setFieldValue("to", toDateStr);

    onFilterSubmit({
      ...props.values,
      timePeriod,
      from: fromDateStr,
      to: toDateStr,
    });
  };

  const clearSearchRecords = () => {
    filterFormRef.current?.resetForm({
      values: initializeFilter(),
    });
    setFilter(initializeFilter());
  };

  return (
    <>
      <div className="xgs-terminal-view-pickup-summary">
        <div className="xgs-list__controls">
          <div className="xgs-list__controls__search">
            <Formik
              initialValues={filter}
              onSubmit={onFilterSubmit}
              validationSchema={PickupsLogFilterSchema}
              innerRef={filterFormRef}
            >
              {(props: FormikProps<TerminalViewPickupSummaryFilterListModel>) => (
                <Form className="xgs-list__controls__search-form">
                  <XGSFormSelect
                    name="timePeriod"
                    label="Time Period:"
                    labelMode={LabelModes.column}
                    placeholder="Select..."
                    options={getTimePeriodListValues()}
                    value={getTimePeriodListValues().find(
                      (option) => option.value === props.values.timePeriod
                    )}
                    onValueChange={(option) => handleTimePeriodChange(option ?? null, props)}
                    className="xgs-item-form__field"
                    isClearable
                  />
                  <LabeledDateRangeInput
                    label="Pickup Date Range:"
                    labelMode={LabelModes.column}
                    className="xgs-item-form__field"
                    start={props.values.from || undefined}
                    end={props.values.to || undefined}
                    maxDate={new Date()}
                    onStartChange={(v) => {
                      props.setFieldValue("from", v || undefined);

                      const newTimePeriod = getTimePeriodFromDates(v || undefined, props.values.to);
                      props.setFieldValue("timePeriod", newTimePeriod);

                      props.submitForm();
                    }}
                    onEndChange={(v) => {
                      props.setFieldValue("to", v || undefined);

                      const newTimePeriod = getTimePeriodFromDates(props.values.from, v || undefined);
                      props.setFieldValue("timePeriod", newTimePeriod);

                      props.submitForm();
                    }}
                  />
                  <XGSFormSelect
                    label="Region:"
                    name="region"
                    placeholder="All Regions"
                    onValueChange={(_region: XGSSelectOption | null | undefined) => {
                      props.setFieldValue("region", _region ?? null);
                    }}
                    value={
                      props.values.region
                        ? getRegionsOptions().find(opt => opt.value === props.values.region)
                        : null
                    }
                    options={getRegionsOptions()}
                    className="xgs-labeled-region-select"
                    labelMode={LabelModes.column}
                  />
                  <LabeledSelectInput
                    label="Origin Terminal:"
                    labelMode={LabelModes.column}
                    isMulti
                    isClearable
                    async
                    isSearchable={false}
                    isLoading={originterminalsLoading}
                    onInputChange={(input: string) => setTerminalSearch(input)}
                    onMultiValuesChange={(option) => {
                      props.setFieldValue("originTerminalNumbers", option ?? []);
                    }}
                    value={props.values.originTerminalNumbers || []}
                    options={getOriginTerminalOptions(props.values.originTerminalNumbers || [])}
                    className="xgs-item-form__field"
                    disabled={originterminalsLoading}
                  />
                  <LabeledSelectInput
                    label="Destination Terminal:"
                    labelMode={LabelModes.column}
                    isMulti
                    isClearable
                    async
                    isSearchable={false}
                    isLoading={destinationterminalsLoading}
                    onInputChange={(input: string) => setDestTerminalSearch(input)}
                    onMultiValuesChange={(option) => {
                      props.setFieldValue("destinationTerminalNumbers", option ?? []);
                    }}
                    value={props.values.destinationTerminalNumbers || []}
                    options={getDestinationTerminalOptions(props.values.destinationTerminalNumbers || [])}
                    className="xgs-item-form__field"
                    disabled={destinationterminalsLoading}
                  />
                  <Button
                    type="submit"
                    theme={ButtonThemes.blue}
                    disabled={
                      (pickupAssignmentState.requestStarted && (pickupAssignmentState.requestCreator === "GET_PICKUPS_LOG"))
                    }
                  >
                    Search
                  </Button>
                  <Button
                    theme={ButtonThemes.gray}
                    onClick={clearSearchRecords}
                  >
                    Clear Filters
                  </Button>
                </Form>
              )
              }
            </Formik >
          </div >
        </div >
        <div className="xgs-list__summary">
          {pickupSummaryTiles && (
            <PicklistsSummary
              picklistSummary={pickupSummaryTiles}
              isLoading={tilesLoading}
            />
          )}
        </div>
        <Table
          isLoading={listLoading}
          columns={columns}
          data={pickupSummaryList?.content || []}
          rowHeight={48}
          minTableHeight={220}
          noResultsText="No Pickup Summary List"
          infiniteScroll
          responsive
        />
        {
          pickupAssignmentState.requestFailed && (
            <XGSErrorMessage className="xgs-pickup-summary__error">{pickupAssignmentState.requestError || "Error while obtaining the log"}</XGSErrorMessage>
          )
        }
      </div >

      {terminalViewNumber ?
        <TerminalViewPickupDetails
          terminalNumber={terminalViewNumber}
          terminalName={
            pickupSummaryList?.content.find(
              (t) => t.terminalNumber === terminalViewNumber
            )?.terminalName
          }
          isOpen={!!terminalViewNumber}
          onClose={() => setTerminalViewNumber(0)}
          filterData={popupFilterData}
          initialStatus={popupFilterData.status}
        /> : null}
    </>
  );
};

export default TerminalViewPickupSummary;